<template>
	<div class="video-player-container" :class="{ minimized: isMinimize, hidden: isHidden, 'nudge-top': isFlowBuilder }">
		<div class="upper-button-bar flex-end--center">
			<AppTooltip text="Hide">
				<AnemicButton @click="onHide" tooltip="minimize">
					<HideSvg />
				</AnemicButton>
			</AppTooltip>
			<AppTooltip :text="isMinimize ? 'Maximize' : 'Minimize'">
				<AnemicButton @click="$emit('setIsMinimized', !isMinimize)">
					<MinimizeVideoSvg :class="{ 'btn-is-minimized': isMinimize }" />
				</AnemicButton>
			</AppTooltip>
		</div>
		<div class="iframe-container">
			<youtube
				:video-id="videoId"
				:player-height="676"
				:player-width="1201"
				:player-vars="{ modestbranding: 1, rel: 0 }"
				:show-related-videos="false"
				@ready="ready"
				@playing="playing"
				@paused="paused"
			/>
		</div>
	</div>
</template>

<script>
import MinimizeVideoSvg from './assets/MinimizeVideoSvg';
import HideSvg from './assets/HideSvg';

export default {
	name: 'VideoPlayerContainer',
	components: {
		MinimizeVideoSvg,
		HideSvg,
	},
	props: {
		tutorial: { type: Object },
		chapters: { type: Array },
		currentChapterIndex: { type: Number },
		isPlaying: { type: Boolean },
		isHidden: { type: Boolean },
		isMinimize: { type: Boolean },
		isChapterStart: { type: Boolean },
		isFlowBuilder: { type: Boolean },
	},
	computed: {
		videoId() {
			return this.tutorial?.video_link.split('?')[1].split('v=')[1].split('&')[0];
		},
	},
	methods: {
		ready(player) {
			this.player = player;
			this.player.target.addEventListener('onStateChange', (e) => this.onVideoStateChange(e));
		},
		playing(/*event*/) {
			this.startIntervalCheck();
			this.$emit('setPausedFromVideo', false);
			this.$emit('setIsMinimized', false);
		},
		start() {
			this.player.target.playVideo();
		},
		stop() {
			this.stopIntervalCheck();
			this.player.target.stopVideo();
		},
		pause() {
			this.stopIntervalCheck();
			this.player.target.pauseVideo();
		},
		paused() {
			this.stopIntervalCheck();
			if (this.isPlaying) {
				this.$emit('setPausedFromVideo', true);
			}
		},
		currentPosition() {
			return this.player.target.getCurrentTime();
		},
		startIntervalCheck() {
			if (this.intervalId === null) {
				this.intervalId = setInterval(() => {
					const currentPosition = this.currentPosition();
					const nextChapterPostion = this.chapters[this.currentChapterIndex + 1];
					if (currentPosition > nextChapterPostion) {
						this.onNewChapterReached();
					}
				}, 1000);
			}
		},
		stopIntervalCheck() {
			clearInterval(this.intervalId);
			this.intervalId = null;
		},
		setCurrentLocation() {
			const currentPosition = this.currentPosition();
			if (currentPosition >= this.chapters.at(-1)) {
				this.$emit('setCurrentChapterIndex', this.chapters.length - 1);
			} else {
				const currentIndex = this.chapters.findIndex((pos, index) => currentPosition >= pos && currentPosition < this.chapters[index + 1]);
				this.$emit('setCurrentChapterIndex', currentIndex);
			}
		},
		onVideoStateChange(event) {
			if (event.data === 1) {
				if (this.isFirstPlay) {
					this.isFirstPlay = false;
					this.player.target.seekTo(this.chapters[this.currentChapterIndex]);
				} else {
					this.setCurrentLocation();
					this.$emit('setIsPlaying', true);
				}
				this.startIntervalCheck();
			}
		},
		onNewChapterReached() {
			this.pause();
			this.$emit('setIsPlaying', false);
			this.setCurrentLocation();
			this.stopIntervalCheck();
			this.$emit('setIsMinimized', true);
			this.$emit('chapterFinished');
		},
		onHide() {
			this.$emit('setIsHidden', true);
			this.$emit('setIsPlaying', false);
		},
	},
	watch: {
		isPlaying: {
			handler(newV) {
				(newV ? this.start : this.pause)();
			},
		},
		currentChapterIndex: {
			handler(newV, oldV) {
				if (newV !== oldV && !this.isPlaying) {
					this.player.target.seekTo(this.chapters[newV]);
				}
			},
		},
		isChapterStart: {
			handler(newV) {
				if (newV) {
					this.player.target.seekTo(this.chapters[this.currentChapterIndex]);
					this.$emit('setIsChapterStart', false);
				}
			},
		},
	},
	mounted() {},
	beforeDestroy() {
		this.stopIntervalCheck();
		this.player?.target.removeEventListener('onStateChange');
	},
	data() {
		return {
			player: null,
			intervalId: null,
			isFirstPlay: true,
		};
	},
};
</script>

<style lang="scss" scoped>
.video-player-container {
	position: absolute;
	transform-origin: bottom left;
	will-change: transform; // performance optimization

	.upper-button-bar {
		position: absolute;
		top: -47px;
		right: 0;

		::v-deep(.v-btn) {
			margin: 0 6px;
		}

		> *:not(:last-child) {
			margin-right: 8px;
		}
	}

	.btn-is-minimized {
		transform: rotate(180deg);
		transition: 0.3s linear 0.4s;
	}

	&:not(.minimized) {
		scale: 1;
		bottom: 50%;
		left: 50%;
		transform: translate(-50%, 50%);
		transition: transform 0.2s, left 0.4s, bottom 0.4s, scale 0.3s linear 0.1s;
	}
	&.minimized {
		scale: 0.35;
		bottom: calc(30px + #{$bottom-player-bar-height});
		left: 20px;
		transform: translate(0%, 0%);
		transition: transform 0.3s linear 0.1s, left 0.4s, bottom 0.4s, scale 0.4s;

		::v-deep(.v-btn) {
			border: 1px solid #dfe4ef;
		}

		.upper-button-bar {
			margin-top: -30px;

			::v-deep(.v-btn) {
				right: 40px;
				margin-left: 100px;
				scale: 2.3;
			}
		}

		&.nudge-top {
			bottom: calc(80px + #{$bottom-player-bar-height});
		}
	}

	&.hidden {
		opacity: 0;
		scale: 0.1;
		bottom: 0;
		transition: bottom 0.4s, scale 0.3s, opacity 0.3s;
	}

	.iframe-container {
		@media (max-height: 810px), (max-width: 1350px) {
			::v-deep(iframe) {
				max-height: 300px;
				max-width: 600px;
			}
		}
	}
}
</style>
